import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext, MouseSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable as originUseSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { SCNavigationProps } from './index';

function Dnd({
  items,
  children,
  onDnd,
  enableDnd,
}: {
  items: SCNavigationProps['items'];
  children: React.ReactNode;
  onDnd?: SCNavigationProps['onDnd'];
  enableDnd?: boolean;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(MouseSensor),
  );
  const modifiers = [restrictToVerticalAxis, restrictToParentElement];

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!active || !over) return;

    if (!items) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    onDnd?.({ activeValue: activeId, overValue: overId });
  };

  if (!enableDnd) {
    return <>{children}</>;
  }

  return (
    <DndContext onDragEnd={handleDragEnd} sensors={sensors} modifiers={modifiers}>
      {children}
    </DndContext>
  );
}

function Sortable({ values, children }: { values: string[]; children: React.ReactNode }) {
  return <SortableContext items={values}>{children}</SortableContext>;
}

function useSortable(props: { value: string }) {
  const { isDragging, attributes, listeners, setNodeRef, transition, transform } =
    originUseSortable({
      id: props.value,
      transition: { duration: 25, easing: 'cubic-bezier(1, 1, 0.5, 1)' },
    });

  const style = transform
    ? ({
        transition,
        transform: CSS.Translate.toString(transform),
        // keep on the top
        zIndex: isDragging ? 10 : undefined,
        position: 'relative',
      } as React.CSSProperties)
    : {};

  return {
    isDragging,
    attributes,
    listeners,
    style,
    ref: setNodeRef,
  };
}

export { Dnd, Sortable, useSortable };
